import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SearchPicker } from '@/components/SearchPicker';
import { ExerciseMedia } from '@/components/ExerciseMedia';
import { toast } from 'sonner';
import { Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';

let exerciseMapPromise = null;
export function loadExerciseMap() {
  if (!exerciseMapPromise) {
    exerciseMapPromise = api.get('/exercises').then(r => {
      const m = {}; (r.data || []).forEach(e => { m[e.id] = e; }); return m;
    }).catch(e => { exerciseMapPromise = null; throw e; });
  }
  return exerciseMapPromise;
}

const toRow = (it, exMap) => ({
  exercise_id: it.exercise_id, exercise_name: exMap[it.exercise_id]?.name || it.exercise_name || 'Exercise',
  sets: it.sets ?? 3, reps: String(it.reps ?? '10'), weight: it.weight || '', rest_seconds: it.rest_seconds ?? 60, notes: it.notes || '',
});

const BUILD_DEFAULTS = {
  'Strength Training': { sets: 3, reps: '10', rest_seconds: 90 },
  HIIT: { sets: 1, reps: '40s', rest_seconds: 20 },
  'Fitness Racing': { sets: 1, reps: '1 km', rest_seconds: 0 },
};

// Edit a client's copy of a session (mode="edit") or build an extra workout (mode="build").
export function SessionEditor({ open, onOpenChange, mode = 'edit', assignment, buildType = 'Strength Training', defaultDate, onSaved }) {
  const [exMap, setExMap] = useState({});
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [date, setDate] = useState(defaultDate || format(new Date(), 'yyyy-MM-dd'));
  const [rounds, setRounds] = useState(3);
  const [saving, setSaving] = useState(false);
  const type = mode === 'build' ? buildType : (assignment?.workout_snapshot?.workout_type);

  useEffect(() => {
    if (!open) return;
    loadExerciseMap().then(m => {
      setExMap(m);
      if (mode === 'edit') {
        const w = assignment.custom_snapshot || assignment.workout_snapshot || {};
        setRows((w.items || []).map(it => toRow(it, m))); setNotes(w.client_notes || '');
      } else {
        setRows([]); setNotes(''); setName(''); setRounds(buildType === 'Strength Training' ? 1 : 3);
        setDate(defaultDate || format(new Date(), 'yyyy-MM-dd'));
      }
    }).catch(() => toast.error('Could not load the exercise library'));
  }, [open]);

  const set = (i, k, v) => setRows(r => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, d) => setRows(r => { const n = [...r]; const t = n[i + d]; if (!t) return r; n[i + d] = n[i]; n[i] = t; return n; });
  const add = (ex) => {
    if (!ex?.id) return;
    setRows(r => [...r, { exercise_id: ex.id, exercise_name: ex.name, weight: '', notes: '', ...(BUILD_DEFAULTS[type] || BUILD_DEFAULTS['Strength Training']) }]);
  };

  const save = async () => {
    if (!rows.length) return toast.error('Add at least one exercise');
    const items = rows.map(r => ({ ...r, sets: Math.max(1, parseInt(r.sets) || 1), rest_seconds: Math.max(0, parseInt(r.rest_seconds) || 0), reps: String(r.reps || '').trim() || '1' }));
    setSaving(true);
    try {
      if (mode === 'edit') await api.put(`/workouts/assignments/${assignment.id}/customize`, { items, notes });
      else await api.post('/workouts/custom-session', { type, date, name, rounds: parseInt(rounds) || 1, items, notes });
      toast.success(mode === 'edit' ? 'Your session was saved' : 'Extra workout added');
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const library = Object.values(exMap).map(e => ({ id: e.id, name: e.name, category: e.muscle_group || e.category }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={e => e.preventDefault()} className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="session-editor">
        <DialogHeader><DialogTitle>{mode === 'edit' ? 'Edit your session' : `Build a ${type === 'Strength Training' ? 'Strength' : type} workout`}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground -mt-2">
          {mode === 'edit' ? "Changes apply to this day's session only. Your coach's original plan is kept and can be restored any time." : 'Recorded as extra training — it never replaces your planned sessions.'}
        </p>
        {mode === 'build' && <BuildMeta {...{ name, setName, date, setDate, rounds, setRounds, type }} />}
        <div className="space-y-2" data-testid="editor-rows">
          {rows.length === 0 && <div className="text-sm text-muted-foreground text-center py-4 regen-card">No exercises yet — add one below.</div>}
          {rows.map((r, i) => <EditorRow key={`${r.exercise_id}-${i}`} i={i} r={r} ex={exMap[r.exercise_id]} last={i === rows.length - 1} set={set} move={move} remove={() => setRows(x => x.filter((_, j) => j !== i))} />)}
        </div>
        <div>
          <div className="label-mini mb-1.5">Add exercise from library</div>
          <SearchPicker items={library} value="" onPick={add} allowFreeText={false} clearOnPick placeholder="Search exercises (squat, row, wall ball…)" testid="editor-add-exercise" />
        </div>
        <div>
          <div className="label-mini mb-1.5">Session notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. Home gym today, only dumbbells" data-testid="editor-notes"
            className="w-full bg-[var(--c-film)] border border-[var(--c-hair)] rounded-xl px-3 py-2 text-sm outline-none focus:ring-1 ring-[#00C8FF]" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="editor-save" className="w-full bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-xl h-11">
          {saving ? 'Saving…' : mode === 'edit' ? 'Save my session' : 'Add workout'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function BuildMeta({ name, setName, date, setDate, rounds, setRounds, type }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="col-span-2"><div className="label-mini mb-1">Workout name</div>
        <Input value={name} onChange={e => setName(e.target.value)} placeholder={`My ${type} session`} data-testid="editor-name" className="bg-[var(--c-film)] border-[var(--c-hair)]" /></div>
      <div><div className="label-mini mb-1">Date</div>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="editor-date" className="bg-[var(--c-film)] border-[var(--c-hair)]" /></div>
      {type !== 'Strength Training' && <div><div className="label-mini mb-1">Rounds</div>
        <Input type="number" min={1} value={rounds} onChange={e => setRounds(e.target.value)} data-testid="editor-rounds" className="bg-[var(--c-film)] border-[var(--c-hair)] font-mono" /></div>}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>{children}</label>;
}

function EditorRow({ i, r, ex, last, set, move, remove }) {
  const cls = 'h-9 bg-[var(--c-film)] border-[var(--c-hair)] font-mono text-sm px-2';
  return (
    <div className="regen-card p-3" data-testid={`editor-row-${i}`}>
      <div className="flex items-center gap-2">
        {ex?.gif_url ? <ExerciseMedia src={ex.gif_url} alt={r.exercise_name} className="w-10 h-10 rounded-lg" fit="cover" /> : <div className="w-10 h-10 rounded-lg bg-[var(--c-film)]" />}
        <div className="flex-1 min-w-0 font-medium text-sm truncate">{r.exercise_name}</div>
        <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`editor-up-${i}`} className="p-1 text-muted-foreground disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
        <button onClick={() => move(i, 1)} disabled={last} data-testid={`editor-down-${i}`} className="p-1 text-muted-foreground disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
        <button onClick={remove} data-testid={`editor-remove-${i}`} className="p-1 text-muted-foreground hover:text-[#FF5C5C]"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-2">
        <Field label="Sets"><Input type="number" min={1} value={r.sets} onChange={e => set(i, 'sets', e.target.value)} data-testid={`editor-sets-${i}`} className={cls} /></Field>
        <Field label="Reps"><Input value={r.reps} onChange={e => set(i, 'reps', e.target.value)} data-testid={`editor-reps-${i}`} className={cls} /></Field>
        <Field label="Weight"><Input value={r.weight} onChange={e => set(i, 'weight', e.target.value)} placeholder="kg" data-testid={`editor-weight-${i}`} className={cls} /></Field>
        <Field label="Rest (s)"><Input type="number" min={0} value={r.rest_seconds} onChange={e => set(i, 'rest_seconds', e.target.value)} data-testid={`editor-rest-${i}`} className={cls} /></Field>
      </div>
      <Input value={r.notes} onChange={e => set(i, 'notes', e.target.value)} placeholder="Notes (optional)" data-testid={`editor-ex-notes-${i}`} className="mt-2 h-9 bg-[var(--c-film)] border-[var(--c-hair)] text-sm" />
    </div>
  );
}
